import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GenericCountyParser } from "./shared/GenericCountyParser";
import type { CountyAdapter, ParsedDocument } from "./shared/types";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Sparkles } from "lucide-react";

interface Props {
  countyId: string;
  countyLabel: string;
  onImportComplete?: () => void;
}

/**
 * Universal AI-driven parser. Sends pasted text to the `ai-county-parser`
 * edge function, which uses Lovable AI Gateway with tool calling to extract
 * standardized records — works for any county/format without a custom adapter.
 */
export function AiUniversalParser({
  countyId,
  countyLabel,
  onImportComplete,
}: Props) {
  const adapter = useMemo<CountyAdapter>(
    () => ({
      countyId,
      countyLabel: `🤖 AI Auto-Parser — ${countyLabel}`,
      description:
        "Paste any county records text (any format). AI extracts the records, then the standard filters/dedupe/import pipeline runs.",
      placeholder:
        "Paste records from any source — tab-separated, multi-line blocks, CSV-like, copy/paste from a court records page...",
      confidenceScore: 70,
      detectionReasons: ["AI Universal Parser", countyLabel],
      filterRules: [
        "AI extracts: documentNumber, filingDate, grantors, grantees, addresses",
        "Standard filters apply: family/business/bank/excluded entity rejection",
        "Existing document numbers are skipped automatically",
        "If results look wrong, try a smaller paste or use the county-specific parser",
      ],
      parse: async (rawText: string): Promise<ParsedDocument[]> => {
        const { data, error } = await supabase.functions.invoke(
          "ai-county-parser",
          {
            body: { text: rawText, countyId, countyLabel },
          },
        );
        if (error) throw new Error(error.message || "AI parser failed");
        if (data?.error) throw new Error(data.error);
        const records = Array.isArray(data?.records) ? data.records : [];
        return records.map((r: any) => ({
          documentNumber: String(r.documentNumber || "").trim(),
          documentType: r.documentType || undefined,
          filingDate:
            r.filingDate || new Date().toISOString().split("T")[0],
          grantors: Array.isArray(r.grantors)
            ? r.grantors.filter(Boolean)
            : [],
          grantees: Array.isArray(r.grantees)
            ? r.grantees.filter(Boolean)
            : [],
          propertyAddress: r.propertyAddress || undefined,
          grantorAddress: r.grantorAddress || undefined,
          granteeAddress: r.granteeAddress || undefined,
          legalDescription: r.legalDescription || undefined,
          notes: r.notes || undefined,
        }));
      },
    }),
    [countyId, countyLabel],
  );

  return (
    <div className="space-y-4">
      <Alert className="border-primary/40">
        <Sparkles className="h-4 w-4" />
        <AlertTitle>AI Auto-Parser Active</AlertTitle>
        <AlertDescription className="text-xs">
          Records will be extracted by AI and saved under{" "}
          <strong>{countyLabel}</strong>. Best for new counties or formats
          without a dedicated parser. Larger pastes take longer; if it stalls,
          paste a smaller batch.
        </AlertDescription>
      </Alert>
      <GenericCountyParser
        adapter={adapter}
        onImportComplete={onImportComplete}
      />
    </div>
  );
}
